import { defineStore, acceptHMRUpdate } from "pinia";
import { useEntityAuditStore } from "@modernary/mui/components/audit";
import { useEntityNoteStore } from "@modernary/mui/components/note";
import { generateComputedResource } from "@modernary/mui/utils/helpers";
import type { FacilitySite } from "./facilitySite";
import { computed, ref } from "vue";
import type { MaterialForForm } from "../materialAccepted/materialAccepted";
import { useEntityAffiliationStore } from "../affiliation/affiliationStore";

export const useFacilitySiteStore = defineStore("facilitySiteStore", () => {
  const entityNoteStore = useEntityNoteStore();
  const entityAuditStore = useEntityAuditStore();
  const entityAffiliationStore = useEntityAffiliationStore();

  // state
  const facilitySite = ref<FacilitySite | null>(null);
  const materialsAccepted = ref<Array<MaterialForForm>>([]);
  const materialsAcceptedCount = ref(0);
  const facilitySiteUsersCount = ref(0);
  const inspectionsCount = ref(0);
  const applicationsCount = ref(0);
  const complaintsCount = ref(0);
  const disposalReportsCount = ref(0);
  // getters
  const authResource = generateComputedResource(facilitySite, computed);
  // actions
  function fill(entity: FacilitySite) {
    if (entity.id !== facilitySite.value?.id) {
      materialsAccepted.value = [];
    }

    facilitySite.value = entity;
    entityNoteStore.fill(entity);
    entityAuditStore.fill(entity, { entityType: "facilitySite", entityId: entity.id });
    entityAffiliationStore.fill(entity);
  }

  function reset() {
    facilitySite.value = null;
    materialsAccepted.value = [];
    materialsAcceptedCount.value = 0;
    facilitySiteUsersCount.value = 0;
    inspectionsCount.value = 0;
    applicationsCount.value = 0;
    complaintsCount.value = 0;
    disposalReportsCount.value = 0;

    entityAuditStore.reset();
    entityNoteStore.reset();
    entityAffiliationStore.reset();
  }

  return {
    // state
    facilitySite,
    materialsAccepted,
    materialsAcceptedCount,
    facilitySiteUsersCount,
    inspectionsCount,
    applicationsCount,
    complaintsCount,
    disposalReportsCount,
    // getters
    authResource,
    // actions
    fill,
    reset,
  };
});
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useFacilitySiteStore, import.meta.hot));
}
